/**
 * PDF-Vorlagen der Agentur (Inhaber 30.10.2026, Vorbild Zoho Books „PDF Templates“): Aussehen von Rechnung, Gutschrift,
 * Stornorechnung, Mahnschreiben, Angebot und Vertrag in einem festen Rahmen anpassen. Die Vorlage regelt nur die Gestaltung
 * (Design, Schriftgröße, Farbe, Logo, Spalten, Beschriftungen, zusätzliche Texte, Bankblock, Girocode, Fuß). Was ein Beleg
 * rechtlich enthalten muss (§ 14 UStG: Nummer, Datum, Leistungszeitraum, Steuer je Satz, Steuerhinweise, Zahlteil der
 * QR-Rechnung), steht immer drin und lässt sich nicht ausblenden.
 *
 * Gespeichert wird die Vorlage als jsonb (document_templates.settings, 0456); belegLayout() liest sie tolerant ein: Unbekanntes
 * fällt weg, fehlende Werte nehmen den Standard. Ohne Vorlage sieht jeder Beleg genau so aus wie bisher.
 */
import { encodeQr, qrRuns } from "./qr-code.js";

export type BelegArt = "invoice" | "dunning" | "offer" | "contract";
export type BelegDesign = "klar" | "akzent" | "minimal";
export type BelegSchrift = "klein" | "normal" | "gross";

export interface BelegLayout {
  /** klar: wie bisher (Akzentlinien); akzent: farbiges Band oben und gefüllter Tabellenkopf; minimal: ohne Farbe, feine Linien */
  design: BelegDesign;
  schrift: BelegSchrift;
  /** eigene Akzentfarbe (#RRGGBB); null = Farbe aus dem Branding */
  akzent: string | null;
  logo: { zeigen: boolean; groesse: "s" | "m" | "l"; position: "links" | "rechts" };
  /** Adresszeile des Ausstellers unter dem Namen im Kopf */
  kopfAdresse: boolean;
  /** eigener Titel für Rechnungen (z. B. „Honorarrechnung“); Gutschrift, Storno und Entwurf behalten ihren Titel */
  titel: string | null;
  spalten: { pos: boolean; menge: boolean; einzelpreis: boolean };
  beschriftung: { pos: string | null; leistung: string | null; menge: string | null; einzelpreis: string | null; betrag: string | null };
  /** zusätzlicher Text vor dem Zahlungshinweis (etwa Dank oder Projektbezug) */
  hinweis: string | null;
  /** Bedingungen nach Bankverbindung (etwa Nutzungsrechte, Skonto) */
  bedingungen: string | null;
  /** Bankverbindung als eigener Block (mit QR-Zahlteil der Schweiz bleibt die Zeile immer) */
  bank: boolean;
  /** Girocode (EPC-QR, SEPA-Überweisung) neben der Bankverbindung, nur bei Euro und IBAN */
  girocode: boolean;
  /** zusätzliche Zeile in der Fußzeile */
  fusstext: string | null;
  seitenzahlen: boolean;
}

export const BELEG_LAYOUT_STANDARD: BelegLayout = {
  design: "klar",
  schrift: "normal",
  akzent: null,
  logo: { zeigen: true, groesse: "m", position: "links" },
  kopfAdresse: true,
  titel: null,
  spalten: { pos: true, menge: true, einzelpreis: true },
  beschriftung: { pos: null, leistung: null, menge: null, einzelpreis: null, betrag: null },
  hinweis: null,
  bedingungen: null,
  bank: true,
  girocode: false,
  fusstext: null,
  seitenzahlen: true,
};

export const BELEG_SCHRIFT_FAKTOR: Record<BelegSchrift, number> = { klein: 0.92, normal: 1, gross: 1.08 };

const text = (v: unknown, max: number): string | null => {
  if (typeof v !== "string") return null;
  const s = v.replace(/\r\n?/g, "\n").replace(/[ \t]+/g, " ").trim();
  return s ? s.slice(0, max) : null;
};
const bool = (v: unknown, fallback: boolean): boolean => (typeof v === "boolean" ? v : fallback);
const one = <T extends string>(v: unknown, allowed: readonly T[], fallback: T): T => (allowed.includes(v as T) ? (v as T) : fallback);
const obj = (v: unknown): Record<string, unknown> => (v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : {});

/** Gespeicherte Einstellungen tolerant einlesen: Unbekanntes fällt weg, Fehlendes nimmt den Standard. */
export function belegLayout(input: unknown): BelegLayout {
  const s = obj(input);
  const d = BELEG_LAYOUT_STANDARD;
  const logo = obj(s.logo), spalten = obj(s.spalten), b = obj(s.beschriftung);
  const hex = typeof s.akzent === "string" && /^#[0-9a-f]{6}$/i.test(s.akzent.trim()) ? s.akzent.trim() : null;
  return {
    design: one(s.design, ["klar", "akzent", "minimal"] as const, d.design),
    schrift: one(s.schrift, ["klein", "normal", "gross"] as const, d.schrift),
    akzent: hex,
    logo: {
      zeigen: bool(logo.zeigen, d.logo.zeigen),
      groesse: one(logo.groesse, ["s", "m", "l"] as const, d.logo.groesse),
      position: one(logo.position, ["links", "rechts"] as const, d.logo.position),
    },
    kopfAdresse: bool(s.kopfAdresse, d.kopfAdresse),
    titel: text(s.titel, 40),
    spalten: { pos: bool(spalten.pos, true), menge: bool(spalten.menge, true), einzelpreis: bool(spalten.einzelpreis, true) },
    beschriftung: {
      pos: text(b.pos, 12), leistung: text(b.leistung, 30), menge: text(b.menge, 16), einzelpreis: text(b.einzelpreis, 20), betrag: text(b.betrag, 20),
    },
    hinweis: text(s.hinweis, 600),
    bedingungen: text(s.bedingungen, 1200),
    bank: bool(s.bank, d.bank),
    girocode: bool(s.girocode, d.girocode),
    fusstext: text(s.fusstext, 200),
    seitenzahlen: bool(s.seitenzahlen, d.seitenzahlen),
  };
}

/** Logo-Rahmen in Punkt je Größe (die bisherige Größe ist „m“: höchstens 150 × 48) */
export function belegLogoBox(groesse: BelegLayout["logo"]["groesse"]): { w: number; h: number } {
  return groesse === "s" ? { w: 110, h: 32 } : groesse === "l" ? { w: 200, h: 64 } : { w: 150, h: 48 };
}

/** Farbe mit Weiß gemischt (für den gefüllten Tabellenkopf des Designs „akzent“) */
export function hellerTon(rgb: readonly [number, number, number], anteilWeiss = 0.86): [number, number, number] {
  return [rgb[0] + (1 - rgb[0]) * anteilWeiss, rgb[1] + (1 - rgb[1]) * anteilWeiss, rgb[2] + (1 - rgb[2]) * anteilWeiss];
}

/**
 * Girocode (EPC069-12, Version 002, SEPA Credit Transfer): nur für Euro, eine gültige IBAN und einen positiven Betrag bis
 * 999.999.999,99 €. Ergebnis ist der Inhalt des QR-Codes oder null.
 */
export function girocodeInhalt(input: { name: string; iban: string; bic?: string | null; cents: number; currency: string; reference?: string | null }): string | null {
  const iban = input.iban.replace(/\s+/g, "").toUpperCase();
  if (input.currency !== "EUR" || !/^[A-Z]{2}\d{2}[A-Z0-9]{10,30}$/.test(iban)) return null;
  if (!Number.isFinite(input.cents) || input.cents <= 0 || input.cents > 99_999_999_999) return null;
  const name = input.name.trim().slice(0, 70);
  if (!name) return null;
  const bic = (input.bic ?? "").replace(/\s+/g, "").toUpperCase().slice(0, 11);
  const betrag = `EUR${(input.cents / 100).toFixed(2)}`;
  const zweck = (input.reference ?? "").trim().slice(0, 140);
  return ["BCD", "002", "1", "SCT", bic, name, iban, betrag, "", "", zweck].join("\n");
}

/** Module des Girocodes (Fehlerkorrektur M wie vom EPC empfohlen) als Rechtecke [x, y, w, h] in Modul-Einheiten */
export function girocodeModule(inhalt: string): { size: number; runs: [number, number, number][] } {
  const qr = encodeQr(inhalt, { ecc: "M" });
  return { size: qr.size, runs: qrRuns(qr) };
}
